<template>
  <div>
    <!-- Results Summary Section -->
    <div class="row">
      <div class="col-12">
        <div class="card bg-hero-primary border-0 rounded-3 welcome-box mb-4">
          <div class="card-body p-4">
            <div class="row align-items-center">
              <div class="col-lg-8 col-md-8 col-sm-8">
                <div class="border-bottom mb-3">
                  <h3 class="text-white fw-semibold mb-1">
                    {{ t('fr.tax_results.title_part1', 'Risultati') }} <span class="text-danger-div">{{ t('fr.tax_results.title_part2', 'Calcolo') }}</span>
                  </h3>
                  <p class="text-light">
                    {{ t('fr.tax_results.description', 'Ecco il piano personalizzato che abbiamo creato per te, {username}', { username: username }) }}
                  </p>
                </div>

                <div class="row">
                  <div class="col-lg-6 col-md-6 col-12">
                    <div class="d-flex align-items-center welcome-status-item">
                      <div class="flex-shrink-0">
                        <i class="material-symbols-outlined">savings</i>
                      </div>
                      <div class="flex-grow-1 ms-3">
                        <p class="text-light  mb-0">{{ t('fr.tax_results.accumulated_balance', 'Saldo Accumulato') }}</p>
                        <strong class="text-white fw-bold mb-0">{{ formatCurrencySimple(getCurrentBalance()) }}</strong>
                        
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div class="col-lg-4 col-md-4 col-sm-4">
                <div class="welcome-img text-center text-sm-end mt-4 mt-sm-0">
                  <img src="@/assets/images/welcome.png" alt="welcome" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Tax Widgets Row -->
    <div class="row">
      <div class="col-lg-6">
        <TaxBreakdownChart
          :title="currentYearTitle"
          :chartTaxTotal="calculateChartAmounts(results.current).taxAmount"
          :chartInpsVariableTotal="calculateChartAmounts(results.current).inpsVariableAmount"
          :chartInpsFixedTotal="calculateChartAmounts(results.current).inpsFixedAmount"
          :chartContribSoggettivo="calculateChartAmounts(results.current).contribSoggettivo"
          :chartContribIntegrativo="calculateChartAmounts(results.current).contribIntegrativo"
          :chartContribMaternita="calculateChartAmounts(results.current).contribMaternita"
          :contributionType="calculateChartAmounts(results.current).contributionType"
          :fatturato="results.current?.fatturatoPrecedente || 0"
          :calculationDetails="results.current?.calculationDetails || null"
          :showSummary="true"
          :year="currentYear - 1"
          :summaryData="{
            fatturato: results.current?.fatturatoPrecedente || 0,
            aliquota: calculateFlatTaxRate(results.current),
            redditoImponibile: results.current?.grossTaxableIncome || 0,
            inpsSaldo: results.current?.previousYearInpsSaldoPaid || 0,
            inpsAcconti: results.current?.previousYearInpsAdvancesPaid || 0,
            irpefAcconti: results.current?.previousYearTaxAdvancesPaid || 0,
            totaleImposte: results.current?.totalDue || 0
          }"
        />
      </div>
      <div class="col-lg-6">
        <TaxBreakdownChart
          :title="nextYearTitle"
          :chartTaxTotal="calculateChartAmounts(results.predictions).taxAmount"
          :chartInpsVariableTotal="calculateChartAmounts(results.predictions).inpsVariableAmount"
          :chartInpsFixedTotal="calculateChartAmounts(results.predictions).inpsFixedAmount"
          :chartContribSoggettivo="calculateChartAmounts(results.predictions).contribSoggettivo"
          :chartContribIntegrativo="calculateChartAmounts(results.predictions).contribIntegrativo"
          :chartContribMaternita="calculateChartAmounts(results.predictions).contribMaternita"
          :contributionType="calculateChartAmounts(results.predictions).contributionType"
          :fatturato="results.predictions?.fatturatoPrecedente || results.current?.fatturatoPrecedente || 0"
          :calculationDetails="results.predictions?.calculationDetails || null"
          :showSummary="true"
          :year="currentYear"
          :summaryData="{
            fatturato: results.predictions?.fatturatoPrecedente || results.current?.fatturatoPrecedente || 0,
            aliquota: calculateFlatTaxRate(results.predictions),
            redditoImponibile: results.predictions?.grossTaxableIncome || 0,
            inpsSaldo: results.predictions?.previousYearInpsSaldoPaid || 0,
            inpsAcconti: results.predictions?.previousYearInpsAdvancesPaid || 0,
            irpefAcconti: results.predictions?.previousYearTaxAdvancesPaid || 0,
            totaleImposte: results.predictions?.totalDue || 0
          }"
        />
      </div>
    </div>

    <!-- Deadlines Widget Row -->
    <div class="row">
      <div class="col-12">
        <DeadlinesWidget :results="results" />
      </div>
    </div>

    <!-- Monthly Flow Chart Row -->
    <div class="row mt-0" v-if="liquidityResults">
        <div class="col-12">
            <MonthlyFlowChart :schedule="liquidityResults" />
        </div>
    </div>

    <!-- Liquidity Statistics Boxes -->
    <div v-if="liquidityResults">
      <LiquidityStatsBoxes :schedule="liquidityResults" />
    </div>

    <!-- Liquidity Schedule Table Row -->
    <div class="row mt-0" v-if="liquidityResults">
        <div class="col-12">
            <LiquidityScheduleTable :schedule="liquidityResults" />
        </div>
    </div>

    <!-- Optional: Raw JSON for debugging 
    <div class="row mt-4">
        <div class="col-12">
            <h5>Raw API Data (Debug)</h5>
            <h6>Tax Calculation</h6>
            <pre>{{ JSON.stringify(results, null, 2) }}</pre>
            <h6>Liquidity Plan</h6>
            <pre>{{ JSON.stringify(liquidityResults, null, 2) }}</pre>
        </div>
    </div>-->
  </div>
</template>

<script setup lang="ts">
import { defineProps, computed } from 'vue';
import { t } from '@/utils/i18nHelper';
import { authStore } from '@/stores/auth';
import TaxBreakdownChart from './TaxBreakdownChart.vue';
import DeadlinesWidget from './DeadlinesWidget.vue';
import LiquidityStatsBoxes from './LiquidityStatsBoxes.vue';
import LiquidityScheduleTable from './LiquidityScheduleTable.vue';
import MonthlyFlowChart from './MonthlyFlowChart.vue';

const props = defineProps({
  results: {
    type: Object,
    required: true,
  },
  liquidityResults: { // New prop
    type: Object,
    required: false,
  },
  contributionRegime: {
    type: String,
    required: true,
  },
  formData: { // New prop for wizard form data
    type: Object,
    required: false,
  },
});


const currentYear = new Date().getFullYear();
const nextYear = currentYear + 1;

const username = computed(() => authStore.user.value?.name || '');

const currentYearTitle = computed(() => 
  t('fr.tax_results.current_year_title', `Totale ${currentYear}`, { currentYear })
);

const nextYearTitle = computed(() => 
  t('fr.tax_results.next_year_title', `Previsione ${nextYear}`, { nextYear })
);

// Helper function to calculate correct flat tax rate
const calculateFlatTaxRate = (yearData: any): number => {
  // If API already provides the correct rate, use it
  if (yearData?.aliquotaForfettaria) {
    return yearData.aliquotaForfettaria;
  }
  if (yearData?.aliquotaImpostaSostitutiva) {
    return yearData.aliquotaImpostaSostitutiva;
  }

  // Otherwise calculate based on form data
  if (props.formData?.isStartup && yearData?.yearsActive <= 5) {
    return 5; // 5% for startups within first 5 years
  }

  return 15; // 15% standard rate
};

const calculateChartAmounts = (data: any) => {
  if (!data) return {
    taxAmount: 0,
    inpsVariableAmount: 0,
    inpsFixedAmount: 0,
    contribSoggettivo: 0,
    contribIntegrativo: 0,
    contribMaternita: 0,
    contributionType: 'INPS'
  };

  // Verifica se è una cassa professionale
  const isProfessional = data.contributionBreakdown !== undefined;

  // Per il grafico sommiamo sempre: saldo + acconti dell'anno corrente
  // Questo mostra tutti i versamenti che l'utente deve fare nell'anno
  const taxAmountRaw = (
    (data.saldoImpostaSostitutiva || data.adjustedTaxBalance || 0) +
    (data.firstAccontoImpostaSostitutiva || 0) +
    (data.secondAccontoImpostaSostitutiva || 0)
  );
  const taxAmount = Number(taxAmountRaw.toFixed(2));

  if (isProfessional) {
    // Casse professionali: usa contributionBreakdown
    const breakdown = data.contributionBreakdown;
    return {
      taxAmount,
      inpsVariableAmount: 0,
      inpsFixedAmount: 0,
      contribSoggettivo: Number((breakdown?.soggettivo?.dovuto || 0).toFixed(2)),
      contribIntegrativo: Number((breakdown?.integrativo?.dovuto || 0).toFixed(2)),
      contribMaternita: Number((breakdown?.maternita?.importo || 0).toFixed(2)),
      contributionType: 'PROFESSIONAL'
    };
  } else {
    // INPS: logica esistente
    const inpsVariableAmountRaw = (
      (data.inpsSaldo || 0) +
      (data.firstAccontoINPS || 0) +
      (data.secondAccontoINPS || 0)
    );
    const inpsVariableAmount = Number(inpsVariableAmountRaw.toFixed(2));

    let inpsFixedAmountRaw = 0;
    if (props.contributionRegime === 'IVS_COMMERCIANTI') {
      inpsFixedAmountRaw = 4549.70;
    } else if (props.contributionRegime === 'IVS_ARTIGIANI') {
      inpsFixedAmountRaw = 4460.64;
    }
    const inpsFixedAmount = Number(inpsFixedAmountRaw.toFixed(2));

    return {
      taxAmount,
      inpsVariableAmount,
      inpsFixedAmount,
      contribSoggettivo: 0,
      contribIntegrativo: 0,
      contribMaternita: 0,
      contributionType: 'INPS'
    };
  }
};



const formatCurrency = (value: number) => {
  if (value === null || value === undefined || isNaN(value)) {
    return '€ 0,00';
  }

  const formatted = Math.abs(value).toFixed(2);
  const parts = formatted.split('.');
  const integerPart = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  const decimalPart = parts[1];

  const sign = value < 0 ? '-' : '';
  return sign + '€ ' + integerPart + ',' + decimalPart;
};



const getNextDeadlineCount = () => {
  const today = new Date();
  let count = 0;

  // Count current year deadlines
  if (props.results?.current?.deadlines) {
    count += props.results.current.deadlines.filter((deadline: any) => {
      return new Date(deadline.date) > today;
    }).length;
  }

  // Count next year deadlines
  if (props.results?.predictions?.deadlines) {
    count += props.results.predictions.deadlines.filter((deadline: any) => {
      return new Date(deadline.date) > today;
    }).length;
  }

  return count;
};

const getAvgMonthlyRate = () => {
  return props.liquidityResults?.metadata?.avgRate || null;
};

// Function to get current balance (saldo accumulato) from wizard form data
const getCurrentBalance = () => {
  // Get from wizard form data - this is the input from the user
  if (props.formData?.currentBalance !== undefined && props.formData?.currentBalance !== null) {
    return props.formData.currentBalance;
  }
  // Fallback to 0 if not found
  return 0;
};

// Simplified currency formatter without double € symbol
const formatCurrencySimple = (value: number) => {
  if (value === null || value === undefined || isNaN(value)) {
    return '€0,00';
  }

  return new Intl.NumberFormat('it-IT', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(value);
};
</script>

<style scoped>
/* Add any specific styles for your results display here */
</style>